import { ChangeEvent, JSX, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid2";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import { asTitle } from "utils/Utils";
import { ChangeEvent as UpdateEvent } from "utils/history/Event";

import { ScoreAggregation } from "evaluation/ScoreAggregation";

import { EvaluationScoresWidget } from "./DashboardTypes";

export type ScoresWidgetExtensionProps = {
	widget: EvaluationScoresWidget;
	editable: boolean;

	onChange: (uuid: string, event: UpdateEvent) => void;
};

export default function ScoresWidgetExtension(
	props: ScoresWidgetExtensionProps
): JSX.Element {
	const { t } = useTranslation();

	const handleValueChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const event = new UpdateEvent("/cluster", props.widget.cluster, value);
			props.onChange(props.widget.uuid, event);
		},
		[props.onChange, props.widget.uuid, props.widget.cluster]
	);

	// Which number a scored competence contributes to this widget's pie: its
	// latest score or the mean of all of them (#633). The replace works on
	// widgets saved before the field existed because the server serialises
	// the Java default for them.
	const handleAggregationChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value as ScoreAggregation;
			const event = new UpdateEvent(
				"/scoreAggregation",
				props.widget.scoreAggregation,
				value
			);
			props.onChange(props.widget.uuid, event);
		},
		[props.onChange, props.widget.uuid, props.widget.scoreAggregation]
	);

	return (
		<Grid container alignItems="flex-end" spacing={2}>
			<Grid size={{ xs: 6, sm: 8, md: 9, lg: 10 }}>
				<TextField
					variant="standard"
					fullWidth
					disabled={!props.editable}
					label={asTitle(t("cluster"))}
					value={props.widget.cluster ?? ""}
					onChange={handleValueChange}
				/>
			</Grid>
			<Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
				<TextField
					select
					variant="standard"
					fullWidth
					disabled={!props.editable}
					label={asTitle(t("scoreAggregation"))}
					helperText={t("scoreAggregationInfo")}
					value={
						props.widget.scoreAggregation ??
						ScoreAggregation.LATEST
					}
					onChange={handleAggregationChange}>
					<MenuItem value={ScoreAggregation.LATEST}>
						{t("scoreAggregationLatest")}
					</MenuItem>
					<MenuItem value={ScoreAggregation.AVERAGE}>
						{t("scoreAggregationAverage")}
					</MenuItem>
				</TextField>
			</Grid>
		</Grid>
	);
}
